import React, { useState, useEffect, useCallback } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { ScrollArea } from '../ui/scroll-area';
import { Badge } from '../ui/badge';
import { Skeleton } from '../ui/skeleton';
import { Button } from '../ui/button';
import { 
  Users, 
  Trophy, 
  Star, 
  Clock, 
  MousePointer2, 
  Medal,
  ArrowLeft,
  TrendingUp,
  TrendingDown
} from 'lucide-react';
import { formatTime } from '../../lib/gameUtils';
import { DIFFICULTIES } from '../../lib/constants';
import { cn, isMobileDevice } from '../../lib/utils';
import { logger } from '../../lib/utils';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Format date for display
const formatDate = (isoDate) => {
  if (!isoDate) return null;
  const date = new Date(isoDate);
  const now = new Date();
  const isCurrentYear = date.getFullYear() === now.getFullYear();
  
  const options = { month: 'short', day: 'numeric' };
  if (!isCurrentYear) {
    options.year = 'numeric';
  }
  return date.toLocaleDateString('en-US', options);
};

export function PuzzleScoresModal({ 
  isOpen, 
  onClose, 
  imageUrl,
  challengeCode,
  playerScore,
  title = "All Scores"
}) {
  const [scores, setScores] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  
  const fetchScores = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      let response;
      const isMobile = isMobileDevice();
      
      if (challengeCode) {
        // Fetch challenge leaderboard
        response = await axios.get(`${API}/challenges/${challengeCode}/leaderboard?is_mobile=${isMobile}`);
        setScores(response.data.scores || []);
      } else if (imageUrl) {
        // Fetch scores by image
        response = await axios.get(`${API}/leaderboard/by-image?image_url=${encodeURIComponent(imageUrl)}&is_mobile=${isMobile}`);
        setScores(response.data.scores || []);
      }
    } catch (err) {
      logger.error('Failed to fetch scores:', err);
      setError('Failed to load scores');
    } finally {
      setIsLoading(false);
    }
  }, [challengeCode, imageUrl]);
  
  useEffect(() => {
    if (isOpen && (imageUrl || challengeCode)) {
      fetchScores();
    }
  }, [isOpen, imageUrl, challengeCode, fetchScores]);
  
  const getRankIcon = (index) => {
    if (index === 0) return <Medal className="h-5 w-5 text-yellow-500" />;
    if (index === 1) return <Medal className="h-5 w-5 text-gray-400" />;
    if (index === 2) return <Medal className="h-5 w-5 text-amber-600" />;
    return <span className="w-5 text-center text-muted-foreground text-sm font-medium">{index + 1}</span>;
  };
  
  const getDifficultyBadge = (difficulty) => {
    const config = DIFFICULTIES[difficulty] || DIFFICULTIES.normal;
    return (
      <Badge 
        variant="outline" 
        className={cn(
          "text-xs px-1.5 py-0",
          difficulty === 'easy' && "bg-success/10 text-success border-success/30",
          difficulty === 'normal' && "bg-primary/10 text-primary border-primary/30",
          difficulty === 'hard' && "bg-destructive/10 text-destructive border-destructive/30"
        )}
      >
        {config.badge}
      </Badge>
    );
  };
  
  // Find player's rank if they have a score
  const playerRank = playerScore ? scores.findIndex(s => s.score <= playerScore) : -1;
  const beatenByCount = playerScore ? scores.filter(s => s.score > playerScore).length : 0;
  
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-lg w-[calc(100vw-2rem)] max-w-[calc(100vw-2rem)] sm:w-auto max-h-[90vh] sm:max-h-[85vh] overflow-y-auto box-border" data-testid="puzzle-scores-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-heading">
            <Button 
              variant="ghost" 
              size="icon" 
              onClick={onClose}
              className="h-8 w-8 lg:hidden"
            >
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <Users className="h-5 w-5 text-primary" />
            {title}
          </DialogTitle>
        </DialogHeader>
        
        {/* Player's position summary */}
        {playerScore && scores.length > 0 && (
          <div className={cn(
            "p-3 rounded-lg border mb-2",
            beatenByCount === 0 ? "bg-success/10 border-success/30" : "bg-muted/50"
          )}>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                {beatenByCount === 0 ? (
                  <>
                    <Trophy className="h-5 w-5 text-success" />
                    <span className="font-medium text-success">You're #1!</span>
                  </>
                ) : (
                  <>
                    <TrendingUp className="h-5 w-5 text-primary" />
                    <span className="font-medium">Your rank: #{playerRank === -1 ? scores.length + 1 : playerRank + 1}</span>
                  </>
                )}
              </div>
              <div className="text-right">
                <span className="font-heading font-bold">{Math.round(playerScore)}</span>
                <span className="text-xs text-muted-foreground ml-1">pts</span>
              </div>
            </div>
            {beatenByCount > 0 && (
              <p className="text-xs text-muted-foreground mt-1">
                {beatenByCount} player{beatenByCount > 1 ? 's' : ''} scored higher than you
              </p>
            )}
          </div>
        )}
        
        {isLoading ? (
          <div className="space-y-3">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="flex items-center gap-4 p-3">
                <Skeleton className="h-6 w-6 rounded-full" />
                <Skeleton className="h-4 flex-1" />
                <Skeleton className="h-4 w-16" />
              </div>
            ))}
          </div>
        ) : error ? (
          <div className="text-center py-8 text-muted-foreground">
            <p>{error}</p>
            <Button variant="outline" size="sm" className="mt-4" onClick={fetchScores}>
              Try Again
            </Button>
          </div>
        ) : scores.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            <Users className="h-12 w-12 mx-auto mb-4 opacity-30" />
            <p>No scores yet for this puzzle.</p>
          </div>
        ) : (
          <ScrollArea className="h-[400px] pr-4">
            <div className="space-y-1.5">
              {scores.map((entry, index) => {
                const beatPlayer = playerScore && entry.score > playerScore;
                const isPlayerScore = playerScore && Math.abs(entry.score - playerScore) < 0.1;
                
                return (
                  <div 
                    key={entry.id || index}
                    className={cn(
                      "flex items-center gap-2 p-2.5 rounded-lg border transition-colors",
                      index < 3 && "bg-muted/50",
                      beatPlayer && "bg-destructive/5 border-destructive/20",
                      isPlayerScore && "bg-primary/10 border-primary/30 ring-1 ring-primary/30"
                    )}
                  >
                    {/* Rank */}
                    <div className="w-7 flex justify-center flex-shrink-0">
                      {getRankIcon(index)}
                    </div>
                    
                    {/* Player Info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1.5 flex-wrap">
                        <span className={cn(
                          "font-medium truncate text-sm",
                          beatPlayer && "text-destructive",
                          isPlayerScore && "text-primary"
                        )}>
                          {entry.player_name}
                        </span>
                        {entry.perfect_game && (
                          <Star className="h-3.5 w-3.5 text-warning fill-warning flex-shrink-0" />
                        )}
                        {getDifficultyBadge(entry.difficulty)}
                        {beatPlayer && (
                          <TrendingDown className="h-3.5 w-3.5 text-destructive flex-shrink-0" />
                        )}
                        {isPlayerScore && (
                          <Badge variant="outline" className="text-[10px] px-1 py-0 bg-primary/10 border-primary/30 text-primary">
                            YOU
                          </Badge>
                        )}
                      </div>
                      <div className="flex items-center gap-3 text-xs text-muted-foreground mt-0.5">
                        <span className="flex items-center gap-1">
                          <Clock className="h-3 w-3" />
                          {formatTime(entry.time_seconds)}
                        </span>
                        <span className="flex items-center gap-1">
                          <MousePointer2 className="h-3 w-3" />
                          {entry.moves}
                        </span>
                      </div>
                    </div>
                    
                    {/* Score */}
                    <div className="text-right flex-shrink-0">
                      <div className={cn(
                        "font-heading font-bold",
                        beatPlayer && "text-destructive",
                        isPlayerScore && "text-primary"
                      )}>
                        {Math.round(entry.score)}
                      </div>
                      <div className="text-[10px] text-muted-foreground">pts</div>
                    </div>
                  </div>
                );
              })}
            </div>
          </ScrollArea>
        )}
        
        {/* Total players footer */}
        {!isLoading && !error && scores.length > 0 && (
          <div className="text-center text-xs text-muted-foreground pt-2 border-t">
            {scores.length} player{scores.length > 1 ? 's' : ''} completed this puzzle
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
